'use client';

import cn from 'classnames';
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react/ssr';
import { Field, FieldDescription, FieldError, FieldLabel, Text } from '@/shared/ui/core';
import { Calendar } from '@/shared/ui/core/calendar';
import { CalendarIcon } from '@phosphor-icons/react/dist/ssr';
import { DatePickerProps } from '../model/datepicker.props';
import styles from '@/shared/styles/controls/dropdown-input.module.scss';
import s from './datepicker.module.scss';


const defaultFormat = (date: Date) => date.toLocaleDateString();

export function DatePicker ({
    value,
    defaultValue,
    onChange,
    placeholder = 'Pick a date',
    state = 'default',
    error,
    description,
    disabled,
    disabledDay,
    label,
    required = false,
    className,
    triggerClassName,
    format = defaultFormat,
}: DatePickerProps) {
    const [ open, setOpen ]                   = useState(false);
    const [ internalValue, setInternalValue ] = useState<Date | undefined>(defaultValue ?? value);

    const isError       = state === 'error' || Boolean(error);
    const isSuccess     = state === 'success';
    const isWarning     = state === 'warning';
    const isDefault     = state === 'default';
    const textClassname = cn({
        [styles.default]    : isDefault,
        [styles.destructive]: isError,
        [styles.success]    : isSuccess,
        [styles.warning]    : isWarning,
        [styles.disabled]   : disabled,
    });

    const currentValue = value ?? internalValue;

    const handleSelect = (date: Date | undefined) => {
        setInternalValue(date);
        onChange?.(date);
        if (date) setOpen(false);
    };

    return (
        <Field className={ cn(styles.root, className) }>
            <Popover.Root open={ open } onOpenChange={ setOpen }>
                <Popover.Trigger
                    type="button"
                    disabled={ disabled }
                    className={ cn(styles.trigger, textClassname, triggerClassName) }
                    aria-invalid={ isError }
                >
                    <div className={ styles.trigger_inner }>
                        {
                            label &&
                            <FieldLabel
                                className={ textClassname }
                                required={ required }
                            >
                                { label }
                            </FieldLabel>
                        }

                        <div className={ styles.value_wrapper }>
                            <CalendarIcon size={ 20 }/>

                            {
                                currentValue
                                    ? (
                                        <Text
                                            size="xl"
                                            tone="primary"
                                            weight="semibold"
                                            lineHeight={ 1 }
                                        >
                                            { format(currentValue) }
                                        </Text>
                                    )
                                    : (
                                        <span className={ styles.placeholder }>
                                            { placeholder }
                                        </span>
                                    )
                            }
                        </div>
                    </div>

                    <CaretDownIcon
                        size={ 20 }
                        className={ styles.chevron }
                    />
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Content
                        align="start"
                        sideOffset={ 6 }
                        onCloseAutoFocus={ (e) => e.preventDefault() }
                        className={ cn(styles.content, s.content) }
                    >
                        <Calendar
                            selected={ currentValue }
                            onSelect={ handleSelect }
                            disabled={ disabledDay }
                        />
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>

            {
                isError &&
                error &&
                <FieldError className={ textClassname }>
                    { error }
                </FieldError>
            }

            {
                !isError &&
                description &&
                <FieldDescription className={ textClassname }>
                    { description }
                </FieldDescription>
            }
        </Field>
    );
}
